import React, { useEffect, useState } from 'react';
import { Form, Button, Col, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import { axiosPost } from '../../utils/axiosPost';
import { axiosDelete } from '../../utils/axiosDelete';
import { windowScrollToTop } from '../../utils/windowScrollToTop';

import PageTitle from '../layouts/PageTitle';
import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';
import useNavigationPrompt from '../hooks/useNavigationPrompt';
import { devError } from '../../utils/devLog';

const SecretaryAndCouncilMember = () => {
  const { activeFellowsArray, usersArray } = useStaticCollections();
  const {
    secretariesArray,
    councilMembersArray,
    committeeArray,
    triggerSecretariesRefresh,
    triggerCouncilMembersRefresh,
  } = useAdminCollections();

  const [notification, setNotification] = useState(null);
  const [isSecretariesDirty, setIsSecretariesDirty] = useState(false);
  const [isCouncilMembersDirty, setIsCouncilMembersDirty] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSubmittingSecretaries, setIsSubmittingSecretaries] = useState(false);
  const [isSubmittingCouncilMembers, setIsSubmittingCouncilMembers] =
    useState(false);

  const {
    control: controlSecretaries,
    setValue: setValueSecretaries,
    handleSubmit: handleSubmitSecretaries,
  } = useForm();
  const {
    control: controlCouncilMembers,
    setValue: setValueCouncilMembers,
    handleSubmit: handleSubmitCouncilMembers,
  } = useForm();

  const isCouncilMembersEmpty =
    Array.isArray(councilMembersArray) && councilMembersArray.length === 0;

  useEffect(() => {
    const processedSecretaries = [];
    Array.isArray(secretariesArray) &&
      secretariesArray.forEach((secretary) => {
        const user = secretary.attributes?.users_permissions_user?.data;
        if (user) {
          const selectedFellow = activeFellowsArray.find(
            (fellow) => fellow.id === parseInt(user.attributes?.fellowId),
          );
          if (selectedFellow) {
            processedSecretaries.push({
              value: secretary.id,
              label: selectedFellow.attributes.label,
            });
          }
        }
      });
    setValueSecretaries('secretaries', processedSecretaries);
  }, [secretariesArray, setValueSecretaries, activeFellowsArray]);

  useEffect(() => {
    const processedCouncilMembers = [];
    Array.isArray(councilMembersArray) &&
      councilMembersArray.forEach((councilMember) => {
        const user = councilMember.attributes?.users_permissions_user?.data;
        if (user) {
          const selectedFellow = activeFellowsArray.find(
            (fellow) => fellow.id === parseInt(user.attributes?.fellowId),
          );
          if (selectedFellow) {
            processedCouncilMembers.push({
              value: councilMember.id,
              label: selectedFellow.attributes.label,
            });
          }
        }
      });

    if (isCouncilMembersEmpty && Array.isArray(committeeArray)) {
      const councilObservers = [];

      committeeArray.forEach((committee) => {
        const observers = committee.attributes?.councilObservers?.data || [];

        observers.forEach((observer) => {
          if (observer.id) {
            const user = usersArray.find((u) => u.id === observer.id);

            if (user) {
              const selectedFellow = activeFellowsArray.find(
                (fellow) => fellow.id === user.fellowId,
              );

              if (
                selectedFellow &&
                !councilObservers.some((co) => co.value === observer.id)
              ) {
                councilObservers.push({
                  value: observer.id,
                  label: selectedFellow.attributes.label,
                });
              }
            }
          }
        });
      });

      setValueCouncilMembers('councilMembers', councilObservers);

      if (councilObservers.length > 0) {
        setNotificationTitle('Council Members Pre-populated');
        setNotificationMessage(
          'Council Members have been pre-populated with Council Observers. Please review and submit to save these selections.',
        );
      }
    } else {
      setValueCouncilMembers('councilMembers', processedCouncilMembers);
    }
  }, [
    councilMembersArray,
    setValueCouncilMembers,
    activeFellowsArray,
    committeeArray,
    usersArray,
    isCouncilMembersEmpty,
  ]);

  useEffect(() => {
    setIsSecretariesDirty(false);
  }, [secretariesArray]);

  useEffect(() => {
    setIsCouncilMembersDirty(false);
  }, [councilMembersArray]);

  useEffect(() => {
    if (
      !isCouncilMembersEmpty &&
      (isSecretariesDirty || isCouncilMembersDirty)
    ) {
      setNotificationTitle('Unsaved Changes');
      setNotificationMessage(
        'To save changes, you are required to press the submit button.',
      );
    } else if (
      !isCouncilMembersEmpty &&
      !isSecretariesDirty &&
      !isCouncilMembersDirty
    ) {
      setNotificationTitle('');
      setNotificationMessage('');
    }
  }, [isSecretariesDirty, isCouncilMembersDirty, isCouncilMembersEmpty]);

  const navigationPrompt = useNavigationPrompt(
    isSecretariesDirty || isCouncilMembersDirty,
    'You have unsaved changes. Are you sure you want to leave this page?',
  );

  const getExcludedUserIdsForCouncilMembers = () => {
    const excludedUserIds = new Set();

    Array.isArray(committeeArray) &&
      committeeArray.forEach((committee) => {
        const chairId = committee.attributes?.committeeChair?.data?.id;
        if (chairId) {
          excludedUserIds.add(chairId);
        }
      });

    return excludedUserIds;
  };

  const getSecretarySelectOptions = () => {
    const assignedUserIds = new Set(
      Array.isArray(secretariesArray)
        ? secretariesArray
            .map((sec) => sec?.attributes?.users_permissions_user?.data?.id)
            .filter(Boolean)
        : [],
    );

    return activeFellowsArray
      .map((item) => {
        if (item.id) {
          const user = usersArray.find((users) => users.fellowId === item.id);
          if (user && !assignedUserIds.has(user.id)) {
            return {
              value: user.id,
              label: item.attributes.label,
            };
          }
        }
        return null;
      })
      .filter(Boolean);
  };

  const getCouncilMemberSelectOptions = () => {
    const assignedUserIds = new Set(
      Array.isArray(councilMembersArray)
        ? councilMembersArray
            .map((cm) => cm?.attributes?.users_permissions_user?.data?.id)
            .filter(Boolean)
        : [],
    );

    const excludedUserIds = getExcludedUserIdsForCouncilMembers();

    return activeFellowsArray
      .map((item) => {
        if (item.id) {
          const user = usersArray.find((users) => users.fellowId === item.id);
          if (
            user &&
            !assignedUserIds.has(user.id) &&
            !excludedUserIds.has(user.id)
          ) {
            return {
              value: user.id,
              label: item.attributes.label,
            };
          }
        }
        return null;
      })
      .filter(Boolean);
  };

  const onSubmitSecretaries = async (formData, e) => {
    e.preventDefault();

    if (isSubmittingSecretaries) {
      return;
    }

    setIsSubmittingSecretaries(true);

    try {
      const currentSecretaryIds = new Set(
        Array.isArray(secretariesArray)
          ? secretariesArray.map((s) => s.id)
          : [],
      );
      const selectedSecretaryIds = new Set(
        formData.secretaries ? formData.secretaries.map((s) => s.value) : [],
      );

      for (const secretaryId of currentSecretaryIds) {
        if (!selectedSecretaryIds.has(secretaryId)) {
          await axiosDelete(`secretaries/${secretaryId}`);
        }
      }

      for (const secretary of formData.secretaries || []) {
        if (!currentSecretaryIds.has(secretary.value)) {
          await axiosPost('secretaries', {
            data: {
              users_permissions_user: {
                id: secretary.value,
              },
            },
          });
        }
      }

      await triggerSecretariesRefresh();

      setNotification('Secretaries Updated');
      setTimeout(() => {
        setNotification('');
      }, 5000);
      windowScrollToTop();
    } catch (error) {
      devError('Error updating secretaries:', error);
      setNotification('Error updating secretaries');
      setNotificationTitle('Error');
    } finally {
      setIsSubmittingSecretaries(false);
    }
    setIsSecretariesDirty(false);
  };

  const onSubmitCouncilMembers = async (formData, e) => {
    e.preventDefault();

    if (isSubmittingCouncilMembers) {
      return;
    }

    setIsSubmittingCouncilMembers(true);

    try {
      const currentCouncilMemberIds = new Set(
        Array.isArray(councilMembersArray)
          ? councilMembersArray.map((cm) => cm.id)
          : [],
      );
      const selectedCouncilMemberIds = new Set(
        formData.councilMembers
          ? formData.councilMembers.map((cm) => cm.value)
          : [],
      );

      for (const councilMemberId of currentCouncilMemberIds) {
        if (!selectedCouncilMemberIds.has(councilMemberId)) {
          await axiosDelete(`council-members/${councilMemberId}`);
        }
      }

      for (const councilMember of formData.councilMembers || []) {
        if (!currentCouncilMemberIds.has(councilMember.value)) {
          await axiosPost('council-members', {
            data: {
              users_permissions_user: {
                id: councilMember.value,
              },
            },
          });
        }
      }

      await triggerCouncilMembersRefresh();

      setNotification('Council Members Updated');
      setTimeout(() => {
        setNotification('');
      }, 5000);
      windowScrollToTop();
    } catch (error) {
      devError('Error updating council members:', error);
      setNotification('Error updating council members');
      setNotificationTitle('Error');
    } finally {
      setIsSubmittingCouncilMembers(false);
    }
    setIsCouncilMembersDirty(false);
  };

  const handleSecretariesFieldChange = (selectedOption) => {
    setValueSecretaries('secretaries', selectedOption);
    setIsSecretariesDirty(true);
  };

  const handleCouncilMembersFieldChange = (selectedOption) => {
    setValueCouncilMembers('councilMembers', selectedOption);
    setIsCouncilMembersDirty(true);
  };

  const baseSelectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      borderRadius: '0.5rem',
      borderColor: '#B3B3B3',
    }),
  };

  return (
    <>
      {navigationPrompt}
      <PageTitle
        activeMenu="Secretaries & Council Members"
        motherMenu="Home"
        notificationMessage={notificationMessage || notification}
        notificationTitle={notificationTitle}
      />

      <Row>
        <Col xs={12} lg={6}>
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Secretaries</h4>
            </div>
            <div className="card-body text-left">
              <Form onSubmit={handleSubmitSecretaries(onSubmitSecretaries)}>
                <div className="mb-xl-5">
                  <label htmlFor="secretaries" className="form-label">
                    Secretaries
                  </label>
                  <Controller
                    name="secretaries"
                    control={controlSecretaries}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={getSecretarySelectOptions()}
                        onChange={handleSecretariesFieldChange}
                        styles={baseSelectStyles}
                        isMulti
                        className="mt-1"
                        placeholder="Select secretaries..."
                      />
                    )}
                  />
                </div>
                <div className="text-end">
                  <Button
                    variant="success"
                    type="submit"
                    className="standard-width-button"
                    disabled={isSubmittingSecretaries}
                  >
                    {isSubmittingSecretaries ? 'Saving...' : 'Submit'}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Col>

        <Col xs={12} lg={6}>
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Council Members</h4>
            </div>
            <div className="card-body text-left">
              <Form
                onSubmit={handleSubmitCouncilMembers(onSubmitCouncilMembers)}
              >
                <div className="mb-xl-5">
                  <label htmlFor="councilMembers" className="form-label">
                    Council Members
                  </label>
                  <Controller
                    name="councilMembers"
                    control={controlCouncilMembers}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={getCouncilMemberSelectOptions()}
                        onChange={handleCouncilMembersFieldChange}
                        styles={baseSelectStyles}
                        isMulti
                        className="mt-1"
                        placeholder="Select council members..."
                      />
                    )}
                  />
                </div>
                <div className="text-end">
                  <Button
                    variant="success"
                    type="submit"
                    className="standard-width-button"
                    disabled={isSubmittingCouncilMembers}
                  >
                    {isSubmittingCouncilMembers ? 'Saving...' : 'Submit'}
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default SecretaryAndCouncilMember;
